// src/types/restaurantReview.ts
// Restaurant ("Craving") diner review model. One-way (diner → restaurant) and
// open to anyone — no account required, since Zamzi Connect has no customer
// sign-up — with a 30-day per-guest rate limit (identified by an anonymous
// cookie) so the vertical's trust/ranking signal can actually grow. Reviews are
// screened at write time (see lib/fixIt/reviewGuard.ts) and the owner has a
// right of public reply rather than the power to remove a negative review.

export const RESTAURANT_REVIEW_CATEGORY_KEYS = [
  'food_quality',
  'service',
  'ambience',
  'value',
  'wait_time',
] as const;
export type RestaurantReviewCategoryKey = (typeof RESTAURANT_REVIEW_CATEGORY_KEYS)[number];

export const RESTAURANT_REVIEW_TAGS = [
  'fresh_food',
  'generous_portions',
  'friendly_service',
  'quick_service',
  'great_value',
  'good_ambience',
] as const;
export type RestaurantReviewTag = (typeof RESTAURANT_REVIEW_TAGS)[number];

export type RestaurantReviewStatus = 'pending' | 'approved' | 'rejected';

/**
 * A diner's review as stored on the profile document and shown on the public
 * profile. Photos are intentionally not part of this model — diners submit text
 * only (Firebase Storage uploads require auth, and the product has no customer
 * accounts).
 */
export interface RestaurantReview {
  id: string;
  profileId: string;
  /** Diner UID when signed in, otherwise the anonymous guest cookie id. */
  reviewerId?: string;
  reviewerName: string;
  rating: number; // 1–5
  body: string;
  categoryRatings?: Partial<Record<RestaurantReviewCategoryKey, number>>;
  tags?: RestaurantReviewTag[];
  /** True when the diner has a verified, paid, completed booking for a stronger trust signal. */
  isVerifiedGuest: boolean;
  verifiedBookingId?: string;
  status: RestaurantReviewStatus;
  /** The owner's public reply, if they have responded. Right of reply, not deletion. */
  ownerReply?: RestaurantReviewOwnerReply;
  /** ISO string (serialized from Firestore Timestamp for client transfer). */
  createdAt: string;
}

export interface RestaurantReviewOwnerReply {
  body: string;
  /** ISO string. */
  createdAt: string;
  /** ISO string. */
  updatedAt?: string;
}